import { GroupableField } from "./pivotColumns";
import { RawAddressWithIndices } from "./types";

// These types were previously in the generated client but are now owned
// here since pivots are computed entirely client-side.

export type PivotRow = {
  group_value: string;
  location_count?: number | null;
  total_tiv?: number | null;
  percent_of_tiv?: number | null;
  sub_rows?: PivotRow[] | null;
};

export type PivotTableResult = {
  column_name: string;
  sub_column_name?: string | null;
  sub_percent_label?: string | null;
  rows: PivotRow[];
  total_row: PivotRow;
};

export type PivotConfig = {
  columnName: string;
  label: string;
  getValue: (addr: RawAddressWithIndices) => string | undefined | null;
  subColumnName?: string;
  subPercentLabel?: string;
  getSubValue?: (addr: RawAddressWithIndices) => string | undefined | null;
  compare?: (a: string, b: string) => number;
};

// ---------------------------------------------------------------------------
// Hierarchical pivot (single dimension with optional sub-rows)
// ---------------------------------------------------------------------------

/**
 * Build a PivotTableResult from in-memory address data.
 */
export function buildPivotTable(
  addresses: RawAddressWithIndices[],
  config: PivotConfig
): PivotTableResult {
  const groups = new Map<
    string,
    {
      count: number;
      tiv: number;
      subGroups?: Map<string, { count: number; tiv: number }>;
    }
  >();

  let totalCount = 0;
  let totalTiv = 0;

  for (const addr of addresses) {
    const raw = config.getValue(addr);
    const key = raw ?? "UNKNOWN";

    let group = groups.get(key);
    if (!group) {
      group = {
        count: 0,
        tiv: 0,
        subGroups: config.getSubValue ? new Map() : undefined,
      };
      groups.set(key, group);
    }
    group.count++;
    group.tiv += addr.tiv;
    totalCount++;
    totalTiv += addr.tiv;

    if (config.getSubValue && group.subGroups) {
      const subRaw = config.getSubValue(addr);
      const subKey = subRaw ?? "UNKNOWN";
      let sub = group.subGroups.get(subKey);
      if (!sub) {
        sub = { count: 0, tiv: 0 };
        group.subGroups.set(subKey, sub);
      }
      sub.count++;
      sub.tiv += addr.tiv;
    }
  }

  const rows: PivotRow[] = Array.from(groups.entries()).map(([key, g]) => {
    const row: PivotRow = {
      group_value: key,
      location_count: g.count,
      total_tiv: g.tiv,
      percent_of_tiv: totalTiv > 0 ? (100 * g.tiv) / totalTiv : 0,
    };
    if (g.subGroups && g.subGroups.size > 0) {
      const parentTiv = g.tiv;
      row.sub_rows = Array.from(g.subGroups.entries())
        .map(([subKey, sub]) => ({
          group_value: subKey,
          location_count: sub.count,
          total_tiv: sub.tiv,
          percent_of_tiv: parentTiv > 0 ? (100 * sub.tiv) / parentTiv : 0,
        }))
        .sort((a, b) => b.total_tiv - a.total_tiv);
    }
    return row;
  });

  sortByKey(
    rows,
    (r) => r.group_value,
    (r) => r.total_tiv ?? 0,
    config.compare
  );

  const totalRow: PivotRow = {
    group_value: "Total",
    location_count: totalCount,
    total_tiv: totalTiv,
    percent_of_tiv: 100.0,
  };

  return {
    column_name: config.columnName,
    sub_column_name: config.subColumnName ?? null,
    sub_percent_label: config.subPercentLabel ?? null,
    rows,
    total_row: totalRow,
  };
}

/**
 * Sort an array in place by a string group key.
 * Uses the provided comparator, or falls back to TIV descending.
 */
function sortByKey<T>(
  items: T[],
  getKey: (item: T) => string,
  getTiv: (item: T) => number,
  compare?: (a: string, b: string) => number
): void {
  if (compare) {
    items.sort((a, b) => compare(getKey(a), getKey(b)));
  } else {
    items.sort((a, b) => getTiv(b) - getTiv(a));
  }
}

/**
 * Build a PivotConfig from two GroupableFields for the hierarchical view.
 */
export function configFromFields(
  primary: GroupableField,
  secondary?: GroupableField
): PivotConfig {
  return {
    columnName: primary.key,
    label: primary.label,
    getValue: primary.getValue,
    subColumnName: secondary?.label,
    subPercentLabel: secondary ? `% of ${primary.label} TIV` : undefined,
    getSubValue: secondary?.getValue,
    compare: primary.compare,
  };
}

// ---------------------------------------------------------------------------
// Cross-tab (two-dimensional pivot)
// ---------------------------------------------------------------------------

export type CrossTabCell = {
  count: number;
  tiv: number;
};

export type CrossTabRow = {
  rowKey: string;
  cells: Record<string, CrossTabCell>;
  total: CrossTabCell;
};

export type CrossTabResult = {
  rows: CrossTabRow[];
  columnKeys: string[];
  columnTotals: Map<string, CrossTabCell>;
  grandTotal: CrossTabCell;
};

/**
 * Build a cross-tab matrix from in-memory address data.
 * Rows = distinct values of rowField, Columns = distinct values of colField,
 * Cells = { count, tiv } at each intersection.
 */
export function buildCrossTab(
  addresses: RawAddressWithIndices[],
  rowField: GroupableField,
  colField: GroupableField
): CrossTabResult {
  const matrix = new Map<string, Map<string, CrossTabCell>>();
  const colTotals = new Map<string, CrossTabCell>();
  let grandCount = 0;
  let grandTiv = 0;

  for (const addr of addresses) {
    const rk = rowField.getValue(addr) ?? "UNKNOWN";
    const ck = colField.getValue(addr) ?? "UNKNOWN";

    let rowMap = matrix.get(rk);
    if (!rowMap) {
      rowMap = new Map();
      matrix.set(rk, rowMap);
    }

    let cell = rowMap.get(ck);
    if (!cell) {
      cell = { count: 0, tiv: 0 };
      rowMap.set(ck, cell);
    }
    cell.count++;
    cell.tiv += addr.tiv;

    let colTotal = colTotals.get(ck);
    if (!colTotal) {
      colTotal = { count: 0, tiv: 0 };
      colTotals.set(ck, colTotal);
    }
    colTotal.count++;
    colTotal.tiv += addr.tiv;

    grandCount++;
    grandTiv += addr.tiv;
  }

  const columnKeys = Array.from(colTotals.keys());
  sortByKey(
    columnKeys,
    (k) => k,
    (k) => colTotals.get(k)?.tiv ?? 0,
    colField.compare
  );

  const rows: CrossTabRow[] = Array.from(matrix.entries()).map(
    ([rowKey, rowMap]) => {
      const cells: Record<string, CrossTabCell> = {};
      let rowCount = 0;
      let rowTiv = 0;
      for (const [ck, cell] of rowMap) {
        cells[ck] = cell;
        rowCount += cell.count;
        rowTiv += cell.tiv;
      }
      return {
        rowKey,
        cells,
        total: { count: rowCount, tiv: rowTiv },
      };
    }
  );

  sortByKey(
    rows,
    (r) => r.rowKey,
    (r) => r.total.tiv,
    rowField.compare
  );

  return {
    rows,
    columnKeys,
    columnTotals: colTotals,
    grandTotal: { count: grandCount, tiv: grandTiv },
  };
}
